import { Navbar } from './ui/Navbar';
import { ApiClient } from './core/ApiClient';
import { i18n } from './core/I18nManager';
import { ToastManager } from './ui/ToastManager';

class UploadPage {
    private navbar!: Navbar;
    private selectedFile: File | null = null;
    private isLoggedIn: boolean = false;

    constructor() {
        this.init();
    }

    private async init() {
        await i18n.init();
        
        const navbarRoot = document.getElementById('navbar-root');
        if (navbarRoot) {
            this.navbar = new Navbar(navbarRoot);
        }

        this.bindEvents();
        // 初始检查一次身份
        this.checkLogin();

        // 监听来自 Navbar 的身份状态变更事件
        window.addEventListener('auth-status-changed', (e: any) => {
            console.log('Auth status changed event received', e.detail);
            this.updateUploadUI(e.detail.isLoggedIn);
        });
    }

    private async checkLogin() {
        try {
            // 不再依赖 Cookie，直接询问后端
            const user = await ApiClient.get('/api/auth/me', undefined, { skipErrorToast: true });
            this.isLoggedIn = !!user;
        } catch (e) {
            this.isLoggedIn = false;
        }
        this.updateUploadUI(this.isLoggedIn);
    }

    private updateUploadUI(isLoggedIn: boolean) {
        this.isLoggedIn = isLoggedIn;
        const authGuide = document.getElementById('auth-guide');
        const loginOverlay = document.getElementById('login-overlay');
        const accWarning = document.getElementById('acc-warning');
        const createBtn = document.getElementById('createPuzzleBtn') as HTMLButtonElement;

        if (this.isLoggedIn) {
            authGuide?.classList.add('hidden');
            loginOverlay?.classList.add('hidden');
            accWarning?.classList.add('hidden');
            if (createBtn) createBtn.disabled = false;
        } else {
            authGuide?.classList.remove('hidden');
            loginOverlay?.classList.remove('hidden');
            accWarning?.classList.remove('hidden');
            if (createBtn) createBtn.disabled = true;
        }
    }

    private bindEvents() {
        // Sign Up / Login Buttons in Guide
        document.getElementById('signUpBtn')?.addEventListener('click', () => {
            (window as any).authModal?.show('register');
        });
        document.getElementById('alreadyHaveAccountBtn')?.addEventListener('click', () => {
            (window as any).authModal?.show('login');
        });

        // Image Selection
        const dropArea = document.getElementById('drop-area');
        const imageInput = document.getElementById('imageInput') as HTMLInputElement;

        dropArea?.addEventListener('click', () => imageInput.click());

        imageInput?.addEventListener('change', (e) => {
            const file = imageInput.files?.[0];
            if (file) this.handleFileSelect(file);
        });

        // Drag and Drop
        dropArea?.addEventListener('dragover', (e) => {
            e.preventDefault();
            dropArea.classList.add('border-stone-900', 'bg-stone-100');
        });

        dropArea?.addEventListener('dragleave', () => {
            dropArea.classList.remove('border-stone-900', 'bg-stone-100');
        });

        dropArea?.addEventListener('drop', (e) => {
            e.preventDefault();
            dropArea.classList.remove('border-stone-900', 'bg-stone-100');
            const file = e.dataTransfer?.files[0];
            if (file) this.handleFileSelect(file);
        });

        // Preset Tags Click
        const tagInput = document.getElementById('puzzleTags') as HTMLInputElement;
        document.querySelectorAll('.preset-tag').forEach(btn => {
            btn.addEventListener('click', () => {
                const tag = (btn as HTMLElement).dataset.tag;
                if (!tag) return;
                
                let currentVal = tagInput.value.trim();
                const tags = currentVal ? currentVal.split(/\s+/) : [];
                
                if (!tags.includes(tag)) {
                    tags.push(tag);
                    tagInput.value = tags.join(' ') + ' ';
                    tagInput.focus();
                }
            });
        });

        // Create Button
        document.getElementById('createPuzzleBtn')?.addEventListener('click', () => this.handleCreate());
    }

    private handleFileSelect(file: File) {
        if (!file.type.startsWith('image/')) {
            ToastManager.error('Please select an image file.');
            return;
        }
        this.selectedFile = file;

        // Show Preview
        const reader = new FileReader();
        reader.onload = (e) => {
            const preview = document.getElementById('image-preview') as HTMLImageElement;
            const container = document.getElementById('preview-container');
            if (preview && container) {
                preview.src = e.target?.result as string;
                container.classList.remove('hidden');
            }
        };
        reader.readAsDataURL(file);
    }

    private async handleCreate() {
        if (!this.isLoggedIn) return;

        const name = (document.getElementById('puzzleName') as HTMLInputElement).value;
        if (!name) {
            ToastManager.warning('Please enter a puzzle name.');
            return;
        }

        if (!this.selectedFile) {
            ToastManager.warning('Please choose an image.');
            return;
        }

        const tagInput = document.getElementById('puzzleTags') as HTMLInputElement;
        const selectedTags = tagInput.value.trim().split(/\s+/).filter(Boolean);

        const access = (document.getElementById('accessSelect') as HTMLSelectElement).value;

        try {
            // 1. Compression (Simulated - logic from current project usually involves canvas resizing)
            console.log('Compressing image...', this.selectedFile.name);
            const compressedBlob = await this.compressImage(this.selectedFile);

            // 2. Submit to Backend
            const formData = new FormData();
            formData.append('image', compressedBlob, 'puzzle.jpg');
            formData.append('name', name);
            formData.append('tags', JSON.stringify(selectedTags));
            formData.append('access', access);

            const response = await fetch('/api/puzzle/create', {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                ToastManager.success('Puzzle created successfully!');
                setTimeout(() => {
                    window.location.href = 'index.html';
                }, 1500);
            } else {
                throw new Error('Failed to create puzzle');
            }

        } catch (e) {
            console.error(e);
            ToastManager.error('Error creating puzzle. Please try again.');
        }
    }

    private compressImage(file: File): Promise<Blob> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (e) => {
                const img = new Image();
                img.src = e.target?.result as string;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    // 工业标准：限制长边最大 1024px
                    const MAX_SIZE = 1024;
                    if (width > MAX_SIZE || height > MAX_SIZE) {
                        if (width > height) {
                            height *= MAX_SIZE / width;
                            width = MAX_SIZE;
                        } else {
                            width *= MAX_SIZE / height;
                            height = MAX_SIZE;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) return reject('Canvas context failed');
                    
                    // 启用高质量图像平滑
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    
                    ctx.drawImage(img, 0, 0, width, height);

                    // 统一转换为 WebP, 质量 0.8
                    canvas.toBlob((blob) => {
                        if (blob) resolve(blob);
                        else reject('Compression failed');
                    }, 'image/webp', 0.8);
                };
                img.onerror = () => reject('Image load error');
            };
            reader.onerror = () => reject('File read error');
        });
    }
}

new UploadPage();
